import React, { useState, useEffect, useRef } from 'react';
import ClientJobOverview from '../components/client/ClientJobOverview';
import DownloadServiceReportButton from '../components/reports/DownloadServiceReportButton';
import GoogleReviewInvite from '../components/GoogleReviewInvite';
import ServiceEvidenceGallery from '../components/work/ServiceEvidenceGallery';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import {
    doc,
    getDoc,
    collection,
    query,
    where,
    addDoc,
    updateDoc,
    onSnapshot,
    writeBatch,
    deleteDoc,
    orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';
import PaymentForm from '../components/payment/PaymentForm';
import FinalPaymentForm from '../components/payment/FinalPaymentForm';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
    ArrowLeft,
    MapPin,
    Calendar,
    Clock,
    DollarSign,
    User,
    Phone,
    Mail,
    Loader,
    CheckCircle,
    AlertCircle,
    MessageSquare,
    Send,
    FileText,
    Image as ImageIcon,
    CreditCard,
    Trash2,
    Square,
    CheckSquare
} from 'lucide-react';

function StarRating({ rating, setRating, readonly = false }) {
    return (
        <div className="flex space-x-2">
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    onClick={() => !readonly && setRating(star)}
                    disabled={readonly}
                    className={`text-3xl transition-colors ${star <= rating
                        ? 'text-yellow-400'
                        : 'text-gray-300'
                        } ${!readonly && 'hover:text-yellow-300 cursor-pointer'}`}
                >
                    ★
                </button>
            ))}
        </div>
    );
}

export default function JobDetail() {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const { currentUser, userProfile } = useAuth();
    const messagesEndRef = useRef(null);

    const [job, setJob] = useState(null);
    const [contractor, setContractor] = useState(null);
    const [client, setClient] = useState(null); // NEW: Client Data
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sendingMessage, setSendingMessage] = useState(false);

    // Bulk Delete State
    const [selectedMsgIds, setSelectedMsgIds] = useState(new Set());

    // Approval State
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [approvalType, setApprovalType] = useState('approve');
    const [rating, setRating] = useState(5);
    const [feedback, setFeedback] = useState('');
    const [submittingApproval, setSubmittingApproval] = useState(false);

    // Payment State
    const [showPayment, setShowPayment] = useState(false);
    const [showFinalPayment, setShowFinalPayment] = useState(false);
    const [completionError, setCompletionError] = useState('');
    const [quoteBusy, setQuoteBusy] = useState(false);
    const [quoteError, setQuoteError] = useState('');
    const [revisionReason, setRevisionReason] = useState('');

    const isAdmin = userProfile?.role === 'admin';

    // 1. JOB LISTENER
    useEffect(() => {
        if (!jobId) return;
        setLoading(true);
        const unsub = onSnapshot(doc(db, 'workOrders', jobId), (docSnap) => {
            if (docSnap.exists()) {
                const data = { id: docSnap.id, ...docSnap.data() };
                setJob(data);

                // Client access to /contractors/{uid} is intentionally restricted by rules.
                // Show the safely denormalized name from the work order to clients;
                // administrators may fetch private contractor contact details.
                if (data.contractorId) {
                    setContractor({ name: data.contractorName || 'Assigned technician' });
                    if (isAdmin) {
                        getDoc(doc(db, 'contractors', data.contractorId)).then(c => {
                            if (c.exists()) setContractor(c.data());
                        }).catch(err => console.warn('Contractor details unavailable', err));
                    }
                } else {
                    setContractor(null);
                }

                // NEW: Fetch Client Profile
                if (data.clientId) {
                    // Try 'users' collection first (most likely)
                    getDoc(doc(db, 'users', data.clientId)).then(userSnap => {
                        if (userSnap.exists()) {
                            setClient(userSnap.data());
                        } else {
                            // Fallback to 'clients' collection if structured differently
                            getDoc(doc(db, 'clients', data.clientId)).then(clientSnap => {
                                if (clientSnap.exists()) setClient(clientSnap.data());
                            });
                        }
                    });
                }

            } else {
                console.log("Job not found");
            }
            setLoading(false);
        });
        return () => unsub();
    }, [jobId, isAdmin]);

    // 2. MESSAGES LISTENER
    useEffect(() => {
        if (!jobId) return;
        const q = query(collection(db, 'messages'), where('workOrderId', '==', jobId));

        const unsub = onSnapshot(q, (snapshot) => {
            const rawMsgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            // Sort in JS to ensure order without index
            const sortedMsgs = rawMsgs.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            setMessages(sortedMsgs);

            // Mark Read Logic
            if (currentUser) {
                const unreadIds = sortedMsgs
                    .filter(m => !m.read && m.senderId !== currentUser.uid)
                    .map(m => m.id);

                if (unreadIds.length > 0) {
                    const batch = writeBatch(db);
                    unreadIds.forEach(id => {
                        batch.update(doc(db, 'messages', id), { read: true });
                    });
                    batch.commit().catch(e => console.error("Error marking read:", e));
                }
            }
        });
        return () => unsub();
    }, [jobId, currentUser]);

    // 3. Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();

        if (!currentUser) {
            alert("Error: User not logged in.");
            return;
        }
        if (!newMessage.trim()) return;

        setSendingMessage(true);

        try {
            const senderType = isAdmin ? 'admin' : 'client';
            const senderName = isAdmin ? 'Admin' : (userProfile?.contactName || userProfile?.name || 'Client');

            await addDoc(collection(db, 'messages'), {
                workOrderId: jobId,
                senderId: currentUser.uid,
                senderName: senderName,
                senderType: senderType,
                message: newMessage,
                createdAt: new Date().toISOString(),
                read: false
            });

            // Notifications are created by the trusted onNewMessage function.
            setNewMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
            alert(`Failed to send: ${error.message}`);
        } finally {
            setSendingMessage(false);
        }
    };

    // Bulk Delete Logic
    const toggleSelectMsg = (id) => {
        const newSet = new Set(selectedMsgIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedMsgIds(newSet);
    };

    const deleteSelectedMsgs = async () => {
        if (selectedMsgIds.size === 0) return;
        try {
            const batch = writeBatch(db);
            selectedMsgIds.forEach(id => {
                const ref = doc(db, 'messages', id);
                batch.delete(ref);
            });
            await batch.commit();
            setSelectedMsgIds(new Set());
        } catch (error) {
            console.error("Bulk delete failed:", error);
        }
    };

    const handleDeleteMessage = async (messageId) => {
        // No confirmation - instant delete
        try {
            await deleteDoc(doc(db, 'messages', messageId));
        } catch (error) {
            console.error("Error deleting message:", error);
        }
    };

    // Payment & Approval Handlers
    const handlePaymentSuccess = () => {
        // Only Stripe's verified webhook may set depositPaid or advance job status.
        setShowPayment(false);
        alert('Card payment submitted. The job status will update after verification.');
    };

    const handleQuoteAction = async (action) => {
        if (!currentUser || !job?.quote?.version) return;
        setQuoteBusy(true);
        setQuoteError('');
        try {
            const fn = httpsCallable(getFunctions(), action === 'accept' ? 'acceptQuote' : 'requestQuoteRevision');
            await fn({
                jobId: job.id,
                version: job.quote.version,
                ...(action === 'revision' ? { reason: revisionReason.trim() } : {})
            });
            setRevisionReason('');
        } catch (err) {
            setQuoteError(err.message || 'Quote response failed. Please try again.');
        } finally { setQuoteBusy(false); }
    };

    const handlePaymentError = (error) => alert('Payment failed.');

    const handleApprovalButtonClick = (type) => {
        // Final-invoice payment is handled separately from the 50% deposit flow.
        setApprovalType(type);
        setShowApprovalModal(true);
    };

    const handleApproval = async () => {
        if (!currentUser || !job) return;
        setSubmittingApproval(true);
        setCompletionError('');
        try {
            if (approvalType === 'approve') {
                await httpsCallable(getFunctions(), 'approveCompletedWork')({
                    jobId: job.id, rating, feedback: feedback.trim()
                });
            } else {
                await httpsCallable(getFunctions(), 'requestCompletionChanges')({
                    jobId: job.id, reason: feedback.trim()
                });
            }
            setShowApprovalModal(false);
            setFeedback('');
        } catch (error) {
            console.error('Completion approval failed:', error);
            setCompletionError(error.message || 'Unable to record your decision.');
        } finally {
            setSubmittingApproval(false);
        }
    };

    const getStatusInfo = (status) => {
        const statusInfo = {
            pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, description: 'Your service request is pending review' },
            under_review: { color: 'bg-amber-100 text-amber-900 border-amber-200', icon: Clock, description: 'Chuko Logic is reviewing your request' },
            quote_ready: { color: 'bg-blue-100 text-blue-900 border-blue-200', icon: FileText, description: 'A quote is being prepared' },
            quote_sent: { color: 'bg-blue-100 text-blue-900 border-blue-200', icon: FileText, description: 'Quote is ready for your review and acceptance' },
            revision_requested: { color: 'bg-yellow-100 text-yellow-900 border-yellow-200', icon: FileText, description: 'You requested a revised quote' },
            deposit_paid: { color: 'bg-green-100 text-green-900 border-green-200', icon: CheckCircle, description: 'Deposit verified. Scheduling is the next step.' },
            pending_payment: { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: CreditCard, description: 'Awaiting deposit payment' },
            scheduled: { color: 'bg-green-100 text-green-900 border-green-200', icon: Calendar, description: 'Your appointment has been confirmed. See the scheduled date and time below.' },
            pending_review: { color: 'bg-amber-100 text-amber-900 border-amber-200', icon: FileText, description: 'Work finished; service report is awaiting Chuko Logic quality review.' },
            assigned: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: User, description: 'A contractor has been assigned' },
            'in-progress': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Clock, description: 'Work is currently in progress' },
            completed: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, description: 'Job has been completed' },
            cancelled: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: AlertCircle, description: 'This job has been cancelled' }
        };
        return statusInfo[status] || { color: 'bg-gray-100', icon: Clock, description: 'Unknown Status' };
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader className="w-8 h-8 animate-spin text-blue-600" /></div>;

    if (!job) return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Job Not Found</h2>
                <Link to={isAdmin ? "/admin" : "/jobs"} className="text-blue-600 hover:underline">
                    Return to Dashboard
                </Link>
            </div>
        </div>
    );

    const statusInfo = getStatusInfo(job.status);
    const StatusIcon = statusInfo.icon;
    const finalInvoiceCents = job.finalInvoice?.amountCents;
    const needsFinalPayment = job.status === 'completed' && job.clientApproval?.approved === true &&
        job.finalInvoice?.status === 'due' && !job.finalPaymentPaid &&
        Number.isSafeInteger(finalInvoiceCents) && finalInvoiceCents >= 50;

    return (
        <div className="cl-workorder min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Link to={isAdmin ? "/admin" : "/jobs"} className="cl-back-link inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6">
                    <ArrowLeft className="w-5 h-5" /><span>{isAdmin ? "Back to Admin Dashboard" : "Back to My Jobs"}</span>
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* LEFT COLUMN */}
                    <div className="lg:col-span-2 space-y-6">
                        {isAdmin && (<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`rounded-xl shadow-sm border-2 p-6 ${statusInfo.color}`}>
                            <div className="flex items-center space-x-3"><StatusIcon className="w-6 h-6" /><div><h3 className="font-bold text-lg">{job.status.replace(/[_-]/g, ' ').toUpperCase()}</h3><p className="text-sm mt-1">{statusInfo.description}</p></div></div>
                        </motion.div>)}

                        {!isAdmin && <ClientJobOverview job={job} />}

                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="cl-card bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <p className="text-xs font-semibold uppercase tracking-widest text-[#2f79d4] mb-2">Work order {job.workOrderNumber || 'Assigning reference…'}</p>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">{job.serviceType}</h2>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-3"><MapPin className="w-5 h-5 text-gray-400 mt-1" /><div><p className="text-sm font-medium text-gray-700">Location</p><p className="text-gray-900">{job.location}</p></div></div>
                                <div className="flex items-start space-x-3"><Calendar className="w-5 h-5 text-gray-400 mt-1" /><div><p className="text-sm font-medium text-gray-700">Dates</p><p className="text-gray-900">Created: {new Date(job.createdAt).toLocaleDateString()}</p></div></div>
                                {job.description && <div className="flex items-start space-x-3"><FileText className="w-5 h-5 text-gray-400 mt-1" /><div><p className="text-sm font-medium text-gray-700">Description</p><p className="text-gray-900">{job.description}</p></div></div>}
                            </div>
                        </motion.div>

                        {/* Confirmed appointment is separate from the customer's preferred date. */}
                        {job.schedule?.date && (
                            <section className="cl-appointment bg-green-50 border border-green-200 rounded-xl p-6 space-y-3" id="job-appointment" aria-label="Confirmed service appointment">
                                <h3 className="text-lg font-bold text-green-900 flex items-center gap-2"><Calendar className="w-5 h-5" /> Confirmed service appointment</h3>
                                <p className="text-green-950 font-semibold">
                                    {new Date(`${job.schedule.date}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                                </p>
                                <p className="text-green-950">{job.schedule.startTime}–{job.schedule.endTime} · Central Time (Wisconsin)</p>
                                <p className="text-sm text-green-900">Location: {job.location}</p>
                                <p className="text-sm text-green-900">Technician: {job.contractorName || 'Chuko Logic — assignment pending'}</p>
                                {job.schedule.notes && <p className="text-sm text-green-900 whitespace-pre-wrap">Arrival instructions: {job.schedule.notes}</p>}
                                <p className="text-xs text-green-800">For scheduling changes, contact dispatch@chukologic.com or (920) 724-1438.</p>
                            </section>
                        )}

                        {/* Only the admin-approved report is published on client-readable workOrders. */}
                        {job.status === 'completed' && job.serviceReport?.approvedAt && (
                          <section className="cl-card bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4" id="job-report" aria-label="Approved service report">
                            <h3 className="text-xl font-bold text-gray-900">Service Completion Report</h3>
                            <DownloadServiceReportButton job={job} />
                            <p className="text-sm text-green-700">Reviewed and approved by Chuko Logic</p>
                            {/* Only display evidence explicitly attached to the admin-approved report. */}
                            <ServiceEvidenceGallery photos={Array.isArray(job.serviceReport.photos) ? job.serviceReport.photos : []} />
                            {[
                              ['diagnosis', 'Findings / diagnosis'],
                              ['workPerformed', 'Work performed'],
                              ['testsPerformed', 'Testing and verification'],
                              ['equipment', 'Equipment / identifiers'],
                              ['recommendations', 'Recommendations'],
                            ].map(([key, label]) => (
                              <div key={key} className="text-sm">
                                <h4 className="font-semibold text-gray-800">{label}</h4>
                                <p className="whitespace-pre-wrap text-gray-700">{job.serviceReport[key] || 'None documented'}</p>
                              </div>
                            ))}
                            <p className="text-xs text-gray-500">Recommendations are not authorization for additional work. Contact dispatch@chukologic.com to request a separate quote.</p>
                          </section>
                        )}

                        {/* PRE-WORK QUOTE ACCEPTANCE — independent of completion approval. */}
                        {job.quote && (
                          <details id="job-quote" open={job.status === 'quote_sent' || job.status === 'quote_ready'} className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
                            <summary className="cursor-pointer text-xl font-bold text-slate-900 py-1">Service quote · Version {job.quote.version}</summary>
                            <p className="text-sm text-gray-600">Quote status: {String(job.quote.status || 'draft').replace(/_/g, ' ')}</p>
                            {job.status === 'quote_ready' && <p className="text-amber-800 text-sm">A revised quote is being prepared. The previously sent version cannot be accepted at this time.</p>}
                            <div><p className="font-semibold">Scope</p><p className="whitespace-pre-wrap text-gray-700">{job.quote.scope}</p></div>
                            <div><p className="font-semibold">Exclusions</p><p className="whitespace-pre-wrap text-gray-700">{job.quote.exclusions || 'Not specified'}</p></div>
                            <div><p className="font-semibold">Terms</p><p className="whitespace-pre-wrap text-gray-700">{job.quote.terms}</p></div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div><p className="text-gray-600">Quote total</p><strong>${(job.quote.totalCents / 100).toFixed(2)}</strong></div>
                              <div><p className="text-gray-600">50% deposit after approval</p><strong>${(job.quote.depositCents / 100).toFixed(2)}</strong></div>
                            </div>
                            {!isAdmin && job.status === 'quote_sent' && job.quote.status === 'sent' && (
                              <div className="border-t pt-4 space-y-3">
                                <p className="text-sm text-gray-700">Accepting authorizes the displayed quote version, scope, price and terms. A deposit is requested only afterward; no appointment is confirmed yet.</p>
                                <button disabled={quoteBusy} onClick={() => handleQuoteAction('accept')} className="bg-green-700 text-white px-5 py-3 rounded-lg font-semibold disabled:opacity-50">{quoteBusy ? 'Processing…' : 'Accept Quote'}</button>
                                <label className="block text-sm font-semibold">Request a revision
                                  <textarea className="mt-1 w-full border border-gray-300 rounded-lg p-3" rows={3} minLength={5} maxLength={1500} value={revisionReason} onChange={e => setRevisionReason(e.target.value)} placeholder="Explain what should change" />
                                </label>
                                <button disabled={quoteBusy || revisionReason.trim().length < 5} onClick={() => handleQuoteAction('revision')} className="border border-gray-400 px-5 py-2 rounded-lg disabled:opacity-50">Request Revision</button>
                              </div>
                            )}
                            {quoteError && <p className="text-red-700" role="alert">{quoteError}</p>}
                          </details>
                        )}

                        {(job.sitePhotos?.length > 0 || job.completionFiles?.length > 0) && (
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="cl-card bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center space-x-2 mb-4"><ImageIcon className="w-5 h-5 text-gray-600" /><h3 className="text-lg font-bold text-gray-900">Photos</h3></div>
                                {job.sitePhotos?.length > 0 && (
                                    <div className="mb-6"><h4 className="text-sm font-semibold text-gray-700 mb-3">Site Photos</h4><div className="grid grid-cols-2 md:grid-cols-3 gap-3">{job.sitePhotos.map((url, index) => (<a key={index} href={url} target="_blank" rel="noopener noreferrer" className="block relative group"><img src={url} alt={`Site photo ${index + 1}`} className="w-full h-32 object-cover rounded-lg border border-gray-200 hover:border-blue-500 transition-colors" /><div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center"><span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">View Full Size</span></div></a>))}</div></div>
                                )}
                                {job.completionFiles?.length > 0 && (
                                    <div><h4 className="text-sm font-semibold text-gray-700 mb-3">Completion Photos</h4><div className="grid grid-cols-2 md:grid-cols-3 gap-3">{job.completionFiles.map((url, index) => (<a key={index} href={url} target="_blank" rel="noopener noreferrer" className="block relative group"><img src={url} alt={`Completion photo ${index + 1}`} className="w-full h-32 object-cover rounded-lg border border-gray-200 hover:border-blue-500 transition-colors" /><div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center"><span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">View Full Size</span></div></a>))}</div></div>
                                )}
                            </motion.div>
                        )}

                        {/* MESSAGES */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="cl-card bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center space-x-2"><MessageSquare className="w-5 h-5 text-gray-600" /><h3 className="text-lg font-bold text-gray-900">Messages</h3></div>
                                {selectedMsgIds.size > 0 && isAdmin && (
                                    <button onClick={deleteSelectedMsgs} className="text-red-600 text-sm flex items-center hover:underline">
                                        <Trash2 className="w-4 h-4 mr-1" /> Delete Selected ({selectedMsgIds.size})
                                    </button>
                                )}
                            </div>

                            <div className="space-y-4 mb-4 max-h-96 overflow-y-auto pr-2">
                                {messages.length > 0 ? (
                                    messages.map((msg) => {
                                        const isMe = msg.senderId === currentUser.uid;
                                        const isSelected = selectedMsgIds.has(msg.id);
                                        return (
                                            <div key={msg.id} className={`flex items-end space-x-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                                {isAdmin && (
                                                    <button onClick={() => toggleSelectMsg(msg.id)} className="mb-4">
                                                        {isSelected ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4 text-gray-300" />}
                                                    </button>
                                                )}
                                                <div className={`p-4 rounded-lg max-w-[80%] ${isMe ? 'bg-blue-50 ml-auto' : 'bg-gray-50 mr-auto'}`}>
                                                    <div className="flex items-start justify-between mb-2">
                                                        <p className="font-semibold text-sm text-gray-900">{msg.senderName}</p>
                                                        <div className="flex items-center space-x-2">
                                                            <p className="text-xs text-gray-500">{new Date(msg.createdAt).toLocaleString()}</p>
                                                            {isAdmin && (
                                                                <button onClick={() => handleDeleteMessage(msg.id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3 h-3" /></button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <p className="text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                                                </div>
                                            </div>
                                        )
                                    })
                                ) : <p className="text-center text-gray-500 py-8">No messages yet.</p>}
                                <div ref={messagesEndRef} />
                            </div>

                            {(isAdmin || (job.status !== 'cancelled' && job.status !== 'completed')) && (
                                <form onSubmit={handleSendMessage} className="flex space-x-2">
                                    <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} className="flex-1 border rounded-lg px-4 py-2" placeholder="Type a message..." />
                                    <button type="submit" disabled={sendingMessage} className="bg-blue-600 text-white px-4 py-2 rounded-lg"><Send className="w-5 h-5" /></button>
                                </form>
                            )}
                        </motion.div>
                        {/* Public review invitation for all completed client jobs, regardless of rating or payment status. */}
                        {!isAdmin && job.status === 'completed' && <GoogleReviewInvite />}
                    </div>

                    {/* RIGHT SIDEBAR */}
                    <div className="space-y-6">

                        {/* NEW: CLIENT PROFILE CARD */}
                        {client && (
                            <motion.div className="cl-card bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center space-x-2 mb-4"><User className="w-5 h-5 text-blue-600" /><h3 className="text-lg font-bold text-gray-900">Client Profile</h3></div>
                                <div className="space-y-3">
                                    <div>
                                        <p className="font-semibold text-gray-900">{client.name || client.contactName || 'Name not provided'}</p>
                                        <p className="text-sm text-gray-600">{client.companyName || 'No Company'}</p>
                                    </div>
                                    <div className="flex items-center space-x-2 text-sm text-gray-600"><Mail className="w-4 h-4" /><span>{client.email || 'No email'}</span></div>
                                    <div className="flex items-center space-x-2 text-sm text-gray-600"><Phone className="w-4 h-4" /><span>{client.phone || 'No phone'}</span></div>
                                </div>
                            </motion.div>
                        )}

                        <motion.div className="cl-card bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h3 id="job-payment" className="text-lg font-bold mb-4">Cost Summary</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between"><span>Deposit:</span><span className={job.depositPaid ? 'text-green-600' : 'text-orange-600'}>${(job.quote?.depositCents != null ? job.quote.depositCents / 100 : job.depositAmount || 0).toFixed(2)}</span></div>
                                <div className="flex justify-between"><span>Remaining:</span><span className={job.finalPaymentPaid ? 'text-green-600' : 'text-gray-900'}>${(job.finalPaymentPaid ? 0 : (job.finalInvoice?.amountCents != null ? job.finalInvoice.amountCents / 100 : (job.quote?.totalCents != null ? (job.quote.totalCents - job.quote.depositCents) / 100 : job.remainingBalance || 0))).toFixed(2)}</span></div>
                                {job.finalPaymentPaid && <p className="font-bold text-green-800">Paid in full ✓</p>}
                                {!job.finalPaymentPaid && job.finalInvoice?.status === 'due' && <p className="font-semibold text-orange-800">Final invoice issued — balance due</p>}
                                {!job.clientApproval?.approved && job.status === 'completed' && <p className="text-xs text-gray-500">Approve completed work to authorize the remaining 50% invoice.</p>}
                            </div>
                            {!isAdmin && job.status === 'pending_payment' && job.quote?.status === 'accepted' && !job.depositPaid && (
                                <button onClick={() => setShowPayment(true)} className="w-full mt-4 bg-orange-600 text-white px-4 py-2 rounded-lg">Pay Deposit</button>
                            )}
                            {!isAdmin && job.status === 'completed' && job.serviceReportStatus === 'approved' &&
                                !job.finalInvoice && !job.finalPaymentPaid && (
                                <div className="mt-4 space-y-2">
                                    {job.clientApproval?.changesRequested && <p className="text-sm text-amber-800">Changes requested. You may accept once the concerns are resolved.</p>}
                                    <button onClick={() => handleApprovalButtonClick('approve')} className="w-full bg-green-600 text-white px-4 py-2 rounded-lg">Confirm work & issue final invoice</button>
                                    <button onClick={() => handleApprovalButtonClick('change')} className="w-full border px-4 py-2 rounded-lg">Request Changes</button>
                                </div>
                            )}
                            {!isAdmin && needsFinalPayment && (
                                <button onClick={() => setShowFinalPayment(true)} className="w-full mt-4 bg-blue-700 text-white px-4 py-3 rounded-lg font-semibold">Pay Remaining ${(finalInvoiceCents / 100).toFixed(2)}</button>
                            )}
                            {!isAdmin && job.clientApproval?.approved && !job.finalPaymentPaid && !needsFinalPayment && (
                                <p className="mt-3 text-sm text-amber-800">Work approved. Waiting for the final invoice to be available; contact dispatch if this persists.</p>
                            )}
                        </motion.div>

                        {contractor && (
                            <motion.div className="cl-card bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center space-x-2 mb-4"><User className="w-5 h-5 text-gray-600" /><h3 className="text-lg font-bold text-gray-900">Assigned Contractor</h3></div>
                                <div className="space-y-3">
                                    <div><p className="font-semibold text-gray-900">{contractor.name}</p>{contractor.company && <p className="text-sm text-gray-600">{contractor.company}</p>}</div>
                                    {contractor.phone && <div className="flex items-center space-x-2 text-sm text-gray-600"><Phone className="w-4 h-4" /><span>{contractor.phone}</span></div>}
                                    {contractor.email && <div className="flex items-center space-x-2 text-sm text-gray-600"><Mail className="w-4 h-4" /><span>{contractor.email}</span></div>}
                                </div>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>

            {showPayment && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="cl-payment-dialog bg-white p-6 rounded-xl max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">Make Payment</h3>
                        <PaymentForm jobId={job.id} quoteVersion={job.quote?.version} amount={job.quote?.depositCents / 100} onSuccess={handlePaymentSuccess} onError={handlePaymentError} />
                        <button onClick={() => setShowPayment(false)} className="mt-4 w-full border py-2 rounded-lg">Cancel</button>
                    </div>
                </div>
            )}
            {showFinalPayment && needsFinalPayment && !isAdmin && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" style={{ zIndex: 80 }}>
                    <div className="bg-white p-6 rounded-xl max-w-md w-full max-h-[90vh] overflow-auto">
                        <FinalPaymentForm jobId={job.id} expectedAmountCents={finalInvoiceCents} onSuccess={() => setShowFinalPayment(false)} />
                        <button onClick={() => setShowFinalPayment(false)} className="mt-4 w-full border py-2 rounded-lg">Close</button>
                    </div>
                </div>
            )}
            {showApprovalModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="cl-payment-dialog bg-white p-6 rounded-xl max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">{approvalType === 'approve' ? 'Approve' : 'Request Changes'}</h3>
                        {approvalType === 'approve' ? <>
                            <StarRating rating={rating} setRating={setRating} />
                            <p className="mt-3 text-sm text-gray-700">By approving the completed work, you authorize issuance of the final 50% invoice. Your card will not be charged until you explicitly submit payment.</p>
                            <textarea className="mt-3 w-full border p-2 rounded-lg" rows={2} value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Optional completion feedback" />
                        </> : <textarea className="w-full border p-2 rounded-lg" rows={4} value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Describe changes (at least 5 characters)..." />}
                        {completionError && <p role="alert" className="text-sm text-red-700 bg-red-50 p-3 mt-2 rounded-lg">{completionError}</p>}
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => { setShowApprovalModal(false); setCompletionError(''); }} className="flex-1 border py-2 rounded-lg">Cancel</button>
                            <button onClick={handleApproval} disabled={submittingApproval || (approvalType === 'change' && feedback.trim().length < 5)} className="flex-1 bg-blue-600 text-white py-2 rounded-lg disabled:opacity-50">{submittingApproval ? 'Saving…' : 'Submit'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
}